import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Building2, CheckCircle2, PhoneOff } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useTheme } from '../contexts/ThemeContext';
interface LandlineDeactivationScreenProps {
  onBack: () => void;
  onComplete: () => void;
}
export function LandlineDeactivationScreen({
  onBack,
  onComplete
}: LandlineDeactivationScreenProps) {
  const {
    isLight
  } = useTheme();
  const [confirmed, setConfirmed] = useState(false);
  return <div className={`h-full flex flex-col overflow-hidden ${isLight ? 'bg-stone-100' : 'bg-black'}`} data-id="element-3266" data-mp="16b41g5qq9pe1">
      <header className="px-6 pt-6 pb-4" data-id="element-3267" data-mp="1l2cuypxr3uwg">
        <button onClick={onBack} aria-label="Back" className={`p-2 -ml-2 rounded-full transition-colors ${isLight ? 'bg-white text-slate-500 border border-slate-200 hover:text-slate-900' : 'bg-white/5 text-zinc-400 border border-white/10 hover:text-white'}`} data-id="element-3268" data-mp="1g0xbh2b81wg6">
          <ArrowLeft className="w-5 h-5" data-id="element-3269" data-mp="35y5og9se7d75" />
        </button>
      </header>

      <div className="flex-1 px-6 flex flex-col justify-center pb-10" data-id="element-3270" data-mp="3awgzgnufbtqy">
        <AnimatePresence mode="wait" data-id="element-3271" data-mp="3ayjl6vojn0pv">
          {!confirmed ? <motion.div key="guide" initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -12
        }} data-id="element-3272" data-mp="3fuiix9vrhv2y">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 ${isLight ? 'bg-red-50 text-red-500' : 'bg-red-500/10 text-red-400'}`} data-id="element-3273" data-mp="3tsylcomlczfa">
                <PhoneOff className="w-8 h-8" data-id="element-3274" data-mp="7ht8jxyghpf7" />
              </div>
              <h1 className={`text-2xl font-bold tracking-tight mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3275" data-mp="cg4j6awks3yh">
                Turn off landline forwarding
              </h1>
              <p className={`text-sm leading-relaxed mb-7 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-3276" data-mp="2dhki3umicink">
                Vexu will stop answering calls sent from this landline.
              </p>

              <div className={`rounded-3xl p-5 mb-7 ${isLight ? 'bg-white border border-slate-200 shadow-sm' : 'bg-white/[0.04] border border-white/10'}`} data-id="element-3277" data-mp="2mb7arbvf55sj">
                <div className="flex items-start gap-3" data-id="element-3278" data-mp="2w9n2nh29sj33">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-300'}`} data-id="element-3279" data-mp="2r7ec0rcvdt45">
                    <Building2 className="w-5 h-5" data-id="element-3280" data-mp="31754knhvdy1l" />
                  </div>
                  <div data-id="element-3281" data-mp="1av8x0upapvkz">
                    <p className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3282" data-mp="1auheakgsrsc4">
                      Dial from your landline
                    </p>
                    <p className={`text-xs leading-relaxed mt-1 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-3283" data-mp="wqs5gog3qhdt">
                      Pick up the same landline handset and dial:
                    </p>
                    <code className={`block text-2xl font-bold tracking-wide mt-4 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3284" data-mp="rx5ponen5xds">
                      *73
                    </code>
                  </div>
                </div>
              </div>

              <Button fullWidth onClick={() => setConfirmed(true)} data-id="element-3285" data-mp="40jjsmmcxeuh">
                I’ve dialed *73
              </Button>
            </motion.div> : <motion.div key="done" initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="text-center" data-id="element-3286" data-mp="917crdu5c5rc">
              <div className={`w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-3287" data-mp="38s4z1cbrjzs">
                <CheckCircle2 className="w-8 h-8" data-id="element-3288" data-mp="3pk5sped0vp8f" />
              </div>
              <h1 className={`text-2xl font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3289" data-mp="3lfy4diah8baq">
                Forwarding turned off
              </h1>
              <p className={`text-sm leading-relaxed mb-8 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-3290" data-mp="3bpwf4n6e0mu8">
                Your landline should no longer forward calls to Vexu. You can
                activate it again at any time.
              </p>
              <Button fullWidth onClick={onComplete} data-id="element-3291" data-mp="2xr6kde71iz91">
                Return to Home
              </Button>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}